import { useState, useEffect, useCallback } from 'react';
import { Plus, Pin, Pencil, Trash2, StickyNote, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const NOTE_TYPES = {
  general:    { label: 'General',    color: 'var(--text-secondary)' },
  support:    { label: 'Support',    color: 'var(--accent-primary)' },
  compliance: { label: 'Compliance', color: '#f59e0b' },
  financial:  { label: 'Financial',  color: 'var(--accent-success)' },
  other:      { label: 'Other',      color: 'var(--text-muted)' },
};

function NoteCard({ note, onEdit, onDelete, onTogglePin }) {
  const typeInfo = NOTE_TYPES[note.note_type] || NOTE_TYPES.general;

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      border: `1px solid ${note.is_pinned ? 'var(--accent-primary)' : 'var(--border-color)'}`,
      borderRadius: 8,
      padding: '12px 14px',
      marginBottom: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 7px',
              borderRadius: 4, background: 'var(--bg-tertiary)',
              color: typeInfo.color, textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              {typeInfo.label}
            </span>
            {note.is_pinned && (
              <span style={{ fontSize: 11, color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Pin size={10} /> Pinned
              </span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {note.content}
          </p>
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
            {note.admin_name || 'Admin'} · {new Date(note.created_at).toLocaleString()}
            {note.updated_at !== note.created_at && ' · edited'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button onClick={() => onTogglePin(note)} title={note.is_pinned ? 'Unpin' : 'Pin'} style={iconBtnStyle}>
            <Pin size={14} color={note.is_pinned ? 'var(--accent-primary)' : 'var(--text-muted)'} />
          </button>
          <button onClick={() => onEdit(note)} title="Edit" style={iconBtnStyle}>
            <Pencil size={14} color="var(--text-muted)" />
          </button>
          <button onClick={() => onDelete(note)} title="Delete" style={iconBtnStyle}>
            <Trash2 size={14} color="var(--accent-danger)" />
          </button>
        </div>
      </div>
    </div>
  );
}

const iconBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: '4px', borderRadius: 4, display: 'flex', alignItems: 'center',
};

function NoteForm({ initial, onSave, onCancel, loading }) {
  const [content, setContent]   = useState(initial?.content || '');
  const [noteType, setNoteType] = useState(initial?.note_type || 'general');
  const [isPinned, setIsPinned] = useState(initial?.is_pinned || false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    onSave({ content: content.trim(), noteType, isPinned });
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
      <textarea
        value={content}
        onChange={e => setContent(e.target.value)}
        placeholder="Write a note..."
        maxLength={2000}
        rows={4}
        style={{
          width: '100%', resize: 'vertical', padding: '10px 12px',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          border: '1px solid var(--border-color)', borderRadius: 8,
          fontSize: 14, boxSizing: 'border-box',
        }}
      />
      <div style={{ display: 'flex', gap: 10, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={noteType}
          onChange={e => setNoteType(e.target.value)}
          style={{ padding: '6px 10px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13 }}
        >
          {Object.entries(NOTE_TYPES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
          <input type="checkbox" checked={isPinned} onChange={e => setIsPinned(e.target.checked)} />
          Pin this note
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button type="button" onClick={onCancel} style={cancelBtnStyle}>Cancel</button>
          <button type="submit" disabled={loading || !content.trim()} style={saveBtnStyle(loading || !content.trim())}>
            {loading ? 'Saving…' : 'Save Note'}
          </button>
        </div>
      </div>
      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}>
        {content.length}/2000
      </div>
    </form>
  );
}

const cancelBtnStyle = {
  padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border-color)',
  background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13,
};
const saveBtnStyle = (disabled) => ({
  padding: '6px 14px', borderRadius: 6, border: 'none',
  background: disabled ? 'var(--bg-tertiary)' : 'var(--accent-primary)',
  color: disabled ? 'var(--text-muted)' : '#fff',
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
});

// ==================== MAIN PANEL ====================

export default function ClientNotesPanel({ userId }) {
  const [notes, setNotes]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editNote, setEditNote] = useState(null);

  const fetchNotes = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await api.get(`/broker/clients/${userId}/notes`);
      setNotes(res.data.data.notes);
      setTotal(res.data.data.total);
    } catch {
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      await api.post(`/broker/clients/${userId}/notes`, data);
      toast.success('Note added');
      setShowForm(false);
      fetchNotes();
    } catch {
      toast.error('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    setSaving(true);
    try {
      await api.put(`/broker/clients/${userId}/notes/${editNote.id}`, data);
      toast.success('Note updated');
      setEditNote(null);
      fetchNotes();
    } catch {
      toast.error('Failed to update note');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (note) => {
    if (!confirm('Delete this note?')) return;
    try {
      await api.delete(`/broker/clients/${userId}/notes/${note.id}`);
      toast.success('Note deleted');
      fetchNotes();
    } catch {
      toast.error('Failed to delete note');
    }
  };

  const handleTogglePin = async (note) => {
    try {
      await api.patch(`/broker/clients/${userId}/notes/${note.id}/pin`, { isPinned: !note.is_pinned });
      fetchNotes();
    } catch {
      toast.error('Failed to update pin');
    }
  };

  const pinned   = notes.filter(n => n.is_pinned);
  const unpinned = notes.filter(n => !n.is_pinned);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StickyNote size={16} color="var(--accent-primary)" />
          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
            Client Notes {total > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({total})</span>}
          </span>
        </div>
        {!showForm && !editNote && (
          <button onClick={() => setShowForm(true)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
            background: 'var(--accent-primary)', color: '#fff', border: 'none',
            borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
          }}>
            <Plus size={14} /> Add Note
          </button>
        )}
      </div>

      {/* Add form */}
      {showForm && (
        <NoteForm
          onSave={handleCreate}
          onCancel={() => setShowForm(false)}
          loading={saving}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>Loading notes…</div>
      ) : notes.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24, fontSize: 14 }}>
          No notes yet. Add one to keep track of important info about this client.
        </div>
      ) : (
        <>
          {pinned.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent-primary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                📌 Pinned
              </div>
              {pinned.map(note => (
                editNote?.id === note.id
                  ? <NoteForm key={note.id} initial={note} onSave={handleUpdate} onCancel={() => setEditNote(null)} loading={saving} />
                  : <NoteCard key={note.id} note={note} onEdit={setEditNote} onDelete={handleDelete} onTogglePin={handleTogglePin} />
              ))}
            </div>
          )}
          {unpinned.map(note => (
            editNote?.id === note.id
              ? <NoteForm key={note.id} initial={note} onSave={handleUpdate} onCancel={() => setEditNote(null)} loading={saving} />
              : <NoteCard key={note.id} note={note} onEdit={setEditNote} onDelete={handleDelete} onTogglePin={handleTogglePin} />
          ))}
        </>
      )}
    </div>
  );
}